import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';

const STATUS_META = {
  live: { label: 'Live', color: '#FF3B30' },
  upcoming: { label: 'Upcoming', color: '#007AFF' },
  finished: { label: 'Final', color: '#71717a' },
};

function MatchCard({ m, index }) {
  const meta = STATUS_META[m.status] || STATUS_META.upcoming;
  const aWins = m.status === 'finished' && m.score_a > m.score_b;
  const bWins = m.status === 'finished' && m.score_b > m.score_a;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.04 }}
      className="border border-white/10 bg-[#121212]"
      data-testid={`match-card-${m.id}`}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em]">
        <span className="text-white/50">{m.sport} · {m.round}</span>
        <span className="flex items-center gap-2" style={{ color: meta.color }}>
          {m.status === 'live' && <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: meta.color }} />}
          {meta.label}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className={`font-heading text-lg md:text-xl ${aWins ? 'text-white' : 'text-white/80'}`}>{m.team_a}</span>
          <span className={`font-display text-3xl md:text-4xl tabular-nums ${aWins ? 'text-[#007AFF]' : 'text-white'}`}>{m.score_a}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`font-heading text-lg md:text-xl ${bWins ? 'text-white' : 'text-white/80'}`}>{m.team_b}</span>
          <span className={`font-display text-3xl md:text-4xl tabular-nums ${bWins ? 'text-[#007AFF]' : 'text-white'}`}>{m.score_b}</span>
        </div>
      </div>
      <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
        <span>{m.venue}</span>
        <span>{m.start_time}</span>
      </div>
    </motion.div>
  );
}

export default function ScoreboardPage() {
  const [matches, setMatches] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get('/matches').then((r) => { setMatches(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // poll for live feel
    return () => clearInterval(t);
  }, [load]);

  const filtered = filter === 'all' ? matches : matches.filter((m) => m.status === filter);
  const liveCount = matches.filter((m) => m.status === 'live').length;

  return (
    <main className="bg-[#0a0a0a] text-white min-h-screen pt-28 pb-24" data-testid="scoreboard-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B30] animate-pulse" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">{liveCount} matches live now</p>
          </div>
          <h1 className="font-display uppercase text-6xl md:text-8xl lg:text-[10vw] leading-[0.85] tracking-tighter mb-8">Scoreboard</h1>
        </motion.div>

        <div className="flex flex-wrap gap-2 md:gap-3 border-y border-white/10 py-6 mb-12" data-testid="score-filters">
          {['all', 'live', 'upcoming', 'finished'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                filter === s ? 'bg-white text-black border-white' : 'border-white/20 text-white/70 hover:border-white'
              }`}
              data-testid={`score-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="font-mono text-xs uppercase tracking-widest text-white/60 animate-pulse py-24">Loading scores…</div>
        ) : filtered.length === 0 ? (
          <div className="font-mono text-xs uppercase tracking-widest text-white/60 py-24">No {filter} matches.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((m, i) => <MatchCard key={m.id} m={m} index={i} />)}
          </div>
        )}
      </div>
    </main>
  );
}
